import { useCallback } from 'react';
import { 
  ANALYTICS_EVENTS, 
  DEBUG_CONFIG, 
  shouldLoadAnalytics 
} from '../config/analytics';

/**
 * Hook personalizado para integração com Umami Analytics
 * Fornece métodos para tracking de eventos e pageviews
 */
export const useAnalytics = () => {
  /**
   * Faz tracking de eventos customizados
   * @param {string} eventName - Nome do evento
   * @param {object} eventData - Dados adicionais do evento
   */
  const trackEvent = useCallback((eventName, eventData = {}) => {
    // Verificar se o analytics deve ser carregado
    if (!shouldLoadAnalytics()) {
      if (DEBUG_CONFIG.ENABLED) {
        console.log(`${DEBUG_CONFIG.CONSOLE_PREFIX} Analytics desabilitado`);
      }
      return;
    }

    if (typeof window !== 'undefined' && window.umami) {
      try {
        window.umami.track(eventName, eventData);
        
        if (DEBUG_CONFIG.ENABLED && DEBUG_CONFIG.LOG_EVENTS) {
          console.log(`${DEBUG_CONFIG.CONSOLE_PREFIX} Event tracked:`, {
            event: eventName,
            data: eventData,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.warn(`${DEBUG_CONFIG.CONSOLE_PREFIX} Erro ao fazer tracking do evento:`, error);
      }
    } else {
      if (DEBUG_CONFIG.ENABLED) {
        console.warn(`${DEBUG_CONFIG.CONSOLE_PREFIX} Umami não está disponível para tracking`);
      }
    }
  }, []);

  /**
   * Faz tracking de visualizações de página
   * @param {string} url - URL da página
   * @param {string} title - Título da página
   */
  const trackPageView = useCallback((url, title) => {
    if (typeof window !== 'undefined' && window.umami) {
      try {
        window.umami.track(url, { title });
        console.log(`📊 Page view tracked: ${url}`);
      } catch (error) {
        console.warn('Erro ao fazer tracking da página:', error);
      }
    }
  }, []);

  /**
   * Tracking específico para cliques em serviços
   * @param {string} serviceName - Nome do serviço
   * @param {string} serviceSlug - Slug do serviço
   */
  const trackServiceClick = useCallback((serviceName, serviceSlug) => {
    trackEvent(ANALYTICS_EVENTS.SERVICE_CLICK, {
      service_name: serviceName,
      service_slug: serviceSlug,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  /**
   * Tracking para cliques no WhatsApp
   * @param {string} source - Origem do clique (header, footer, service, etc.)
   */
  const trackWhatsAppClick = useCallback((source = 'unknown') => {
    trackEvent(ANALYTICS_EVENTS.WHATSAPP_CLICK, {
      source,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  /**
   * Tracking para envio de formulários
   * @param {string} formType - Tipo do formulário (contact, quote, etc.)
   * @param {object} formData - Dados do formulário (sem informações sensíveis)
   */
  const trackFormSubmit = useCallback((formType, formData = {}) => {
    trackEvent(ANALYTICS_EVENTS.FORM_SUBMIT, {
      form_type: formType,
      ...formData,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  /**
   * Tracking para navegação entre páginas
   * @param {string} from - Página de origem
   * @param {string} to - Página de destino
   */
  const trackNavigation = useCallback((from, to) => {
    trackEvent(ANALYTICS_EVENTS.NAVIGATION, {
      from_page: from,
      to_page: to,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  /**
   * Tracking para downloads
   * @param {string} fileName - Nome do arquivo
   * @param {string} fileType - Tipo do arquivo
   */
  const trackDownload = useCallback((fileName, fileType) => {
    trackEvent('download', {
      file_name: fileName,
      file_type: fileType,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Removido: tracking específico de dashboard (página descontinuada)

  /**
   * Tracking para scroll depth (profundidade de rolagem)
   * @param {number} percentage - Porcentagem da página visualizada
   */
  const trackScrollDepth = useCallback((percentage) => {
    trackEvent('scroll_depth', {
      percentage,
      page: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  /**
   * Verifica se o Umami está disponível
   * @returns {boolean} - True se o Umami estiver carregado
   */
  const isUmamiAvailable = useCallback(() => {
    return typeof window !== 'undefined' && !!window.umami;
  }, []);

  return {
    trackEvent,
    trackPageView,
    trackServiceClick,
    trackWhatsAppClick,
    trackFormSubmit,
    trackNavigation,
    trackDownload,
    trackScrollDepth,
    isUmamiAvailable
  };
};

export default useAnalytics;